import React from "react";
import Slider from "react-slick";
import { Container, Typography, Box, IconButton } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';


const useCases = [
  { title: "Language Learning", description: "Share tips, resources, and request language lessons from verified speakers to enhance your skills." },
  { title: "Software Development", description: "Post code snippets, share resources, and request coding lessons from experts in various programming languages." },
  { title: "Marketing Strategies", description: "Post successful campaigns and request personalized marketing strategies from experienced marketers." },
  { title: "Digital Design & UX/UI", description: "Share design mockups, tips, and request expert guidance on creating user-friendly digital designs." },
  { title: "Public Speaking", description: "Share tips for overcoming stage fright and request sessions with public speaking coaches." },
  { title: "Entrepreneurial Advice", description: "Share tips for starting a business and find investors or mentors to help scale your venture." },
  { title: "Financial Planning & Investment", description: "Post financial advice, budgeting strategies, and request insights into investing and money management." },
  { title: "Health & Fitness", description: "Share workout routines, healthy eating tips, and request personal training or wellness advice." },
  { title: "Career Guidance", description: "Post career growth tips and request mentorship or advice on job hunting, interviews, and career development." },
  { title: "Cooking & Culinary Arts", description: "Share recipes, cooking tips, and request cooking lessons from chefs to enhance your culinary skills." },
  { title: "Art & Painting", description: "Post artwork, share techniques, and request art lessons to improve your skills in painting and drawing." },
  { title: "Science & Technology", description: "Share research articles, experiments, and request guidance on scientific theories or technology trends." },
  { title: "Mental Health & Well-being", description: "Post tips for mental health, mindfulness, and request advice on coping strategies for mental wellness." },
  { title: "Music Lessons", description: "Share music theory, instrument tutorials, and request lessons for playing instruments like guitar or piano." },
  { title: "Photography & Videography", description: "Post photography tips, editing techniques, and request lessons from experts to enhance your skills." },
  { title: "Travel & Adventure", description: "Share travel tips, destination guides, and request travel planning sessions or recommendations." },
  { title: "Environmental Sustainability", description: "Post eco-friendly practices and request advice on sustainable living and green technologies." },
  { title: "History & Culture", description: "Share historical articles, cultural practices, and request lessons or discussions on history and culture." },
  { title: "Yoga & Meditation", description: "Post yoga routines, meditation techniques, and request guidance from certified instructors for personalized sessions." },
  { title: "Coding Bootcamp", description: "Post coding tutorials and request live coding lessons on specific programming languages or frameworks." },
  { title: "Social Media Growth", description: "Share tips for growing social media followers, building engagement, and request mentorship on online branding." },
  { title: "Legal & Contract Advice", description: "Post legal tips, contract templates, and request legal advice from verified lawyers or legal experts." },
  { title: "Graphic Design", description: "Share design resources, tutorials, and request sessions to learn tools like Photoshop or Illustrator." },
  { title: "Personal Development", description: "Post motivational tips, self-improvement strategies, and request personal coaching for growth and productivity." },
  { title: "Writing & Blogging", description: "Share writing tips, blogging strategies, and request sessions to refine writing and storytelling skills." },
  { title: "Fashion & Styling", description: "Post fashion trends, styling advice, and request personalized wardrobe consultations from experts." },
  { title: "E-commerce & Online Selling", description: "Share tips for setting up an online store, increasing sales, and request guidance from e-commerce experts." },
  { title: "Parenting Tips", description: "Post advice on parenting, child development, and request guidance on managing different stages of parenting." },
  { title: "Personal Finance & Budgeting", description: "Share budgeting strategies, saving tips, and request advice on managing personal finances and investments." },
  { title: "Event Planning & Management", description: "Post event planning tips, vendor recommendations, and request advice on organizing large events." }
];


// Custom arrow components
const CustomArrow = ({ onClick, direction }) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: 'absolute',
      top: '50%',
      [direction]: 0,
      transform: 'translateY(-50%)',
      zIndex: 10,
      color: '#1E90FF',
      backgroundColor: 'rgba(0,0,0,0.5)',
      '&:hover': {
        backgroundColor: 'rgba(30,144,255, 0.8)',
        color: '#fff',
      },
    }}
  >
    {direction === 'left' ? <ArrowBackIosNew /> : <ArrowForwardIos />}
  </IconButton>
);

// Slider settings
const settings = {
  dots: false,
  infinite: true,
  speed: 700,
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 6000,
  nextArrow: <CustomArrow direction="right" />,
  prevArrow: <CustomArrow direction="left" />,
  responsive: [
    {
      breakpoint: 1200,
      settings: { slidesToShow: 2 },
    },
    {
      breakpoint: 768,
      settings: { slidesToShow: 1 },
    },
  ],
};


const UseCaseSlider = () => {
 

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 6, sm: 10, md: 12 },
        background: 'linear-gradient(to right, #000000, #000000)',
        position: 'relative',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          textAlign: 'center',
          fontWeight: 500,
          fontSize: { xs: '1.8rem', md: '2rem' },
          color: '#fff',
          textTransform: 'uppercase',
          letterSpacing: 2,
          mb: 5,
          '&:hover': {
            color: '#1E90FF',
          },
        }}
      >
        Discover Various Use Cases
      </Typography>

      <Box
        sx={{
          width: '100%',
          maxWidth: '1300px',
          mx: 'auto',
          px: 2,
          position: 'relative',
        }}
      >
        <Slider {...settings}>
          {useCases.map((useCase, index) => (
            <Box
              key={index}
              sx={{
                px: 2,
                height: '100%',
              }}
            >
              <Box
                sx={{
                  background: 'rgb(3, 3, 3)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                  padding: 4,
                  height: '100%',
                  transition: 'all 0.4s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    borderColor: '#1E90FF',
                    background: 'rgba(30,144,255,0.07)',
                  },
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 400,
                    fontSize: '1.2rem',
                    color: '#fff',
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    textAlign: 'center',
                    '&:hover': {
                      color: '#1E90FF',
                    },
                  }}
                >
                  {useCase.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    color: '#ccc',
                    textAlign: 'center',
                    lineHeight: 1.6,
                    px: 2,
                  }}
                >
                  {useCase.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Slider>
      </Box>
    </Container>
  );
};

export default UseCaseSlider;
